import React, { ReactElement, useEffect, useMemo, useRef, useState } from 'react'
import { Text, Flex } from 'theme-ui'
import ruleTypes from 'components/Dashboard/Profiles/CustomRules/ruleTypes'
import RuleActions from 'components/Dashboard/Profiles/RuleActions'
import { RuleType } from 'store/api/rules'
import { updateCustomRuleDraft, updateGroupDraft } from 'store/customRules'
import ChooseDestinationButton from 'components/Dashboard/Profiles/CustomRules/RuleOrFolderDialog/ChooseDestination'
import { useAppDispatch, useAppSelector } from 'store/hooks'
import { ActionableRule, isCustomRule, isGroupRule } from 'utils/uniteRules'
import ExpiryIcon from 'images/customRules/expiry-icon.svg'
import { setRuleTooltipSettings } from 'store/tutorial/tutorial'
import IconWithTippyPopup from 'ui/IconWithTippyPopup'
import { useGetProxiesQuery } from 'store/api/proxies'
import { useGetUserQuery } from 'store/api/user'
import { Button, Svg } from 'ui'
import { FutureDateTimePicker } from 'ui/DateTimePicker'
import { TooltipWrapper } from 'ui/TooltipWrapper'
import InfoIcon from 'images/dashboard/info-icon.svg'

interface RuleTypeMenuProps {
  rule: ActionableRule
  forceRuleType?: RuleType
}
export default function RuleTypeMenu({ rule, forceRuleType }: RuleTypeMenuProps): ReactElement {
  const dispatch = useAppDispatch()
  const { data: proxiesData } = useGetProxiesQuery('')
  const proxyLocations = proxiesData?.proxies
  const proxyLocationCodes = proxyLocations?.map(p => p.PK)
  const { data: userData } = useGetUserQuery('')
  const userPk = userData?.PK || ''
  const ruleTypeInfo = ruleTypes.find(rt => rt.ruleType === (forceRuleType ?? rule?.action?.do))
  const isBlocked = (forceRuleType ?? rule?.action?.do) === RuleType.BLOCK
  const draftRule = useAppSelector(s => s.ruleTray.draftCustomRule)
  const prevTtlRef = useRef<number>(0)
  const defaultActionTtl =
    !!draftRule.action?.ttl && draftRule.action?.ttl !== -1 ? draftRule.action?.ttl : undefined

  const [shouldShowExpiryDate, setShouldShowExpiryDate] = useState(!!defaultActionTtl)

  useEffect(() => {
    if (defaultActionTtl) {
      prevTtlRef.current = defaultActionTtl
    }
  }, [defaultActionTtl])

  const handleRuleTypeClick = (ruleType: RuleType): void => {
    dispatch(
      setRuleTooltipSettings({
        userPk,
        selectedRuleType: ruleType,
        selectedRuleOrFolderPk: rule?.PK,
      }),
    )
    // fix for #1369. Need to check via and change from Spoof_Tag to Spoof_IP
    if (
      rule.action?.via &&
      rule.action?.via !== '?' &&
      !proxyLocationCodes?.includes(rule.action?.via ?? '') &&
      ruleType === RuleType.SPOOF_TAG
    ) {
      ruleType = RuleType.SPOOF_IP
    }
    if (rule.action?.do !== ruleType) {
      if (isCustomRule(rule)) {
        dispatch(updateCustomRuleDraft({ action: { do: forceRuleType || ruleType } })) // let the via remain as is, so that user gets it back in case redirect is selected again
      }
      if (isGroupRule(rule)) {
        dispatch(updateGroupDraft({ action: { do: ruleType } }))
      }
    }
  }

  // FutureDateTimePicker should be wrapped in useMemo as extra re-renders affect the work of react-datepicker library
  const dateTimePicker = useMemo(
    () =>
      shouldShowExpiryDate && !isGroupRule(rule) ? (
        <FutureDateTimePicker
          date={draftRule.action?.ttl ? new Date(draftRule.action?.ttl * 1000) : null}
          onDateChange={date => {
            dispatch(updateCustomRuleDraft({ action: { ttl: Math.floor(date.getTime() / 1000) } }))
          }}
          minDate={new Date()}
          maxDate={new Date('2200-12-31')}
        />
      ) : null,
    [dispatch, draftRule.action?.ttl, rule, shouldShowExpiryDate],
  )

  return (
    <Flex
      data-testid={isCustomRule(rule) ? 'custom-rule-type-menu' : 'folder-rule-type-menu'}
      sx={{
        flexDirection: 'column',
        px: '1.6rem',
        py: '1.2rem',
      }}
    >
      <Flex
        sx={{
          alignItems: 'center',
          width: '100%',
        }}
      >
        <Flex
          sx={{
            width: '100%',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Flex sx={{ flexDirection: 'column' }}>
            <Flex sx={{ flexDirection: 'row' }}>
              <Text
                variant="size15Weight700"
                data-testid="selected-rule-type-text"
                sx={{ color: isBlocked ? 'pomegranate' : 'greenApple' }}
              >
                {ruleTypeInfo?.text ?? ''}
              </Text>
              {forceRuleType !== undefined && (
                <IconWithTippyPopup
                  content={`Since you're currently adding a rule inside an Action Folder, the rule will be saved using the folder's current action.  If you want to make the rule do something different, please add it to a different folder.`}
                  ariaLabel={`Since you're currently adding a rule inside an Action Folder, the rule will be saved using the folder's current action.  If you want to make the rule do something different, please add it to a different folder.`}
                  className="info-icon"
                  sx={{ width: '1.2rem', height: '1.2rem', ml: '0.5rem' }}
                  svg={InfoIcon}
                  fill="aliceBlue60"
                  descriptiveText="Action Folder Rule Info"
                  tooltipTextVariant="size12Weight400"
                />
              )}
            </Flex>
            {ruleTypeInfo && (
              <Text
                variant="size12Weight400"
                sx={{
                  color: 'aliceBlue60',
                }}
              >
                {ruleTypeInfo.description}
              </Text>
            )}
          </Flex>

          <RuleActions
            rule={rule}
            handleRuleTypeClick={handleRuleTypeClick}
            forceRuleType={forceRuleType}
          />
        </Flex>

        {!isGroupRule(rule) && (
          <Flex
            sx={{
              ml: ['0.8rem', '1.2rem'],
              pl: ['0.8rem', '1.2rem'],
              borderLeft: '1px solid',
              borderLeftColor: 'blueYonder15',
            }}
          >
            <TooltipWrapper
              text={
                <Text
                  variant="size12Weight400Letter018"
                  sx={{ display: 'inline-flex', textAlign: 'center', color: 'aliceBlue' }}
                >
                  Set the rule expiration date
                </Text>
              }
              maxWidth={110}
              offset={[8, 8]}
            >
              <Button
                data-testid="rule-expiry-button"
                ariaLabel="rule expiry button"
                variant="simple"
                sx={{
                  p: 0,
                  'svg > g': {
                    opacity: shouldShowExpiryDate ? 1 : 0.6,
                  },
                  ':hover': {
                    'svg > g': {
                      opacity: 1,
                    },
                  },
                }}
                onClick={(): void => {
                  dispatch(
                    updateCustomRuleDraft({
                      action: {
                        ttl: shouldShowExpiryDate
                          ? -1
                          : prevTtlRef.current || Math.floor(new Date().getTime() / 1000),
                      },
                    }),
                  )
                  setShouldShowExpiryDate(prev => !prev)
                }}
              >
                <Svg svg={ExpiryIcon} fill="aliceBlue" descriptiveText="Set Rule Expiry" />
              </Button>
            </TooltipWrapper>
          </Flex>
        )}
      </Flex>

      {forceRuleType === undefined && (
        <ChooseDestinationButton rule={rule} forceRuleType={forceRuleType} />
      )}

      {dateTimePicker}
    </Flex>
  )
}
